import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { rootContainer } from '../ioc';
import { ClientDataStore } from '../core/stores/client-data.store';
import { LoadingStore } from '../core/stores/loading.store';
import { RouterStore } from '../core/stores/router.store';
import { ExtraAxiosConfig } from '../api';

interface ApiExceptionResponse {
    message: string;
    exceptionMessage: string;
    exceptionType: string;
    stackTrace?: string;
}

const clientData = rootContainer.get(ClientDataStore);
const loading = rootContainer.get(LoadingStore);
const router = rootContainer.get(RouterStore);

axios.interceptors.request.use(
    (config: AxiosRequestConfig & ExtraAxiosConfig) => {
        if (config.showBusy) {
            loading.busy(true);
        }
        return config;
    },
    (config: AxiosRequestConfig & ExtraAxiosConfig) => {
        if (config.showBusy) {
            loading.busy(false);
        }
    }
);

axios.interceptors.response.use(
    (response) => {
        const { showBusy } = response.config as AxiosRequestConfig & ExtraAxiosConfig;
        if (showBusy) {
            loading.busy(false);
        }
        return response;
    },
    (error: AxiosError) => {
        // Do not intercept endpoints specific to the loan application workflow.
        // These have to be handled separately by the financing drawer/page.
        const url = error.config.url;
        if (url?.includes('/api/financing-bridge/')) {
            if (url.endsWith('loans') ||
                url.endsWith('document') ||
                url.endsWith('signatures') ||
                url.endsWith('documentfile') ||
                url.endsWith('selected')) {
                return Promise.reject(error);
            }
        }

        const { showBusy, errorStatusMap } = error.config as AxiosRequestConfig & ExtraAxiosConfig;

        if (showBusy) {
            loading.busy(false);
        }

        // https://github.com/axios/axios#handling-errors
        const response: AxiosResponse<ApiExceptionResponse | undefined> | undefined =
            error.response;
        let errMsg: string;
        if (!response) {
            errMsg = error.message || 'An unknown error occurred.';
        } else if (errorStatusMap && response.status in errorStatusMap) {
            // The backend returned an unsuccessful response code.
            const value = errorStatusMap[response.status];
            const data = response.data;
            let result = typeof value === 'function' ? value() : value;
            if (!value && data && data.exceptionMessage &&
                data.exceptionType === "ApiException") {
                result = data.exceptionMessage;
            }
            if (typeof result === 'string') {
                errMsg = result;
            } else {
                return new Promise<never>(() => { });
            }
        } else if (response.status === 401) {
            router.history.replace(
                clientData.isAnonymousPayments ? '/invoice/expired-link' : '/login',
                { from: router.history.location }
            );
            return new Promise<never>(() => { });
        } else if (response.status === 500) {
            router.history.push('/system-down');
            return new Promise<never>(() => { });
        } else if (!response.data) {
            errMsg = 'An API error occurred';
        } else {
            // The backend returned an unsuccessful response code.
            const exception = response.data;
            errMsg =
                exception.exceptionMessage ||
                exception.message ||
                exception.exceptionType ||
                exception.toString();
        }
        return Promise.reject(errMsg);
    }
);
